import PropTypes from "prop-types";
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from "react";
import { useApolloClient, useQuery } from "@apollo/client";

import CommentForm from "components/CommentForm";
import CommentItem from "components/CommentItem";
import CommentList from "components/CommentList";
import TurnstileWrapper from "components/TurnstileWrapper";

import useLanguageProvider from "components/LanguageProvider/hooks/useLanguageProvider";
import { TURNSTILE_KEY } from "helpers/constants";
import { fetch as router } from "helpers/router";
import request from "utils/request";
import { csrf, captcha } from "utils/requestHeaders";

import classNames from "classnames";

import { GET_CAMPAIGN_COMMENTS } from "queries/hooks";
import CommentContext from "./CommentContext";
import "./_style.scss";
import CommentModal from "../../components/CommentModal";

const getResponseData = (response) => response.data || response;

const buildCommentFormData = (formData, commentData) => {
  const actions = [
    {
      action: "post_comment",
      details: {
        content: commentData.content,
        anonymous: commentData.anonymous,
        campaign_id: commentData.campaignId,
        campaign_update_id: commentData.campaignUpdateId,
        challenger_id: commentData.challengerId,
        reply_to_id: commentData.replyToId,
      },
    },
  ];
  return { ...formData, actions: JSON.stringify(actions) };
};

const normalizeComment = (node) => ({
  id: node.id,
  content: node.content,
  timestamp: node.createdAt,
  full_name: [node.user?.firstName, node.user?.lastName].filter(Boolean).join(" ") || "Anonymous",
  image: node.user?.profileImage ?? null,
  anonymous: node.anonymous ?? false,
  replies: [],
});

/**
 * @param {object} props
 * @param {string|number} props.campaignId
 * @param {string} props.campaignTitle
 * @param {string} [props.campaignUpdateId]
 * @param {string|number} [props.challengerId]
 * @param {object} [props.user]
 * @param {() => void} [props.onCommentAdded]
 */
const Comments = ({
  campaignId,
  campaignTitle,
  campaignUpdateId = undefined,
  challengerId = null,
  user = {},
  onCommentAdded = undefined,
}) => {
  const client = useApolloClient();

  const {
    data,
    loading: isLoading,
    refetch,
  } = useQuery(GET_CAMPAIGN_COMMENTS, {
    variables: { campaignId, campaignUpdateId },
  });

  const [comments, setComments] = useState([]);
  const [pendingComment, setPendingComment] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [commentModalOpen, setCommentModalOpen] = useState(false);
  const [commentModalStep, setCommentModalStepState] = useState("default");
  const [replyToId, setReplyToId] = useState(null);
  const [replyToName, setReplyToName] = useState(null);
  const [signupEmail, setSignupEmail] = useState(null);
  const [signupName, setSignupName] = useState(null);
  const [isSendingLink, setIsSendingLink] = useState(false);
  const [isResendingLink, setIsResendingLink] = useState(false);
  const [isResendLinkFail, setIsResendLinkFail] = useState(false);
  const [isResendLinkSent, setIsResendLinkSent] = useState(false);

  useEffect(() => {
    if (!data?.campaignComments?.edges) {
      return;
    }

    const byId = {};
    data.campaignComments.edges.forEach(({ node }) => {
      byId[node.id] = { ...normalizeComment(node), replies: [] };
    });

    const topLevel = [];
    data.campaignComments.edges.forEach(({ node }) => {
      const parentId = node.parent?.id;
      if (parentId && byId[parentId]) {
        byId[parentId].replies.push(byId[node.id]);
      } else {
        topLevel.push(byId[node.id]);
      }
    });

    setComments(topLevel);
  }, [data]);

  const { translate: t } = useLanguageProvider();

  const [captchaToken, setCaptchaToken] = useState({ token: null });
  const [hasTurnstileError, setHasTurnstileError] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(null);

  const turnstileRef = useRef(null);

  const isLoggedIn = useMemo(
    () => !!(user && Object.hasOwn(user, "name") && user.name),
    [user],
  );

  const resetTurnstileAndToken = useCallback(() => {
    setCaptchaToken({ token: null });
    setHasTurnstileError(false);
    setIsRefreshing(false);
    turnstileRef.current?.reset?.();
  }, []);

  const requestVerification = useCallback(() => {
    setCaptchaToken({ token: null });
    setHasTurnstileError(false);
    setIsRefreshing(true);
    turnstileRef.current?.reset?.();
  }, []);

  const handleSetModalStatus = useCallback((isOpen) => {
    setCommentModalOpen(isOpen);
    if (!isOpen) {
      setCommentModalStepState("default");
      setErrorMessage(null);
      setIsError(false);
    }
  }, []);

  const handleSetModalStep = useCallback((stepName) => {
    setCommentModalStepState(stepName);
    setErrorMessage(null);
    setIsError(false);
  }, []);

  const handleReplyComment = useCallback((id, name) => {
    setReplyToId(id ?? null);
    setReplyToName(name ?? null);
    if (window.location.hash) {
      history.replaceState(null, "", window.location.pathname + window.location.search);
    }
  }, []);

  const handleCreateComment = useCallback(
    async (commentData) => {
      setIsCreating(true);

      let url;
      let formData = {};

      if (commentData.step === "signup") {
        url = router("auth.signup.ajax");
        formData.name = commentData.name;
        formData.email = commentData.email;
        formData.password = commentData.password;
        formData = buildCommentFormData(formData, commentData);
      } else if (commentData.step === "login") {
        url = router("auth.login.ajax");
        formData.email = commentData.email;
        formData.password = commentData.password;
        formData.mfa = { code: commentData.mfaCode, type: "authenticator" };
        formData = buildCommentFormData(formData, commentData);
      } else {
        url = router("web.user.postComment", { campaign: commentData.campaign_id });
        formData = commentData;
      }

      const headers = {
        ...csrf(document),
        ...captcha(commentData.captchaToken, ""),
      };

      const req = request(url, "POST", headers, formData);

      const loginRedirect = (csrfToken) => {
        if (csrfToken) {
          document.head.querySelector("[name=\"csrf-token\"]")?.setAttribute("content", csrfToken);
        }
        setPendingSubmit(null);
        setCommentModalOpen(false);
        setCommentModalStepState("default");
        setIsCreating(false);
        client.refetchQueries({ include: "active" });
      };

      try {
        const response = await fetch(req);
        if ((commentData.step === "login" || commentData.step === "signup") && response.redirected) {
          loginRedirect(null);
          return;
        }
        if (!response.ok) {
          const err = await response.json();
          throw new Error(err.error?.message || "You're unable to post a comment at this time");
        }
        const json = await response.json();
        const responseData = getResponseData(json);

        if (responseData.success === true) {
          if (commentData.step === "login") {
            loginRedirect(responseData.csrf_token ?? null);
          } else {
            const isModalOpen = Object.hasOwn(commentData, "step");
            setCommentModalOpen(isModalOpen);
            setCommentModalStepState(isModalOpen ? "thankyou" : "default");
            if (Array.isArray(responseData.comments)) {
              setComments(responseData.comments);
            }
            // Only show pending comment for modal flows (signup/email link).
            // Direct logged-in posts have no pending state; the session may have
            // a stale email_login_id belonging to a different user.
            if (isModalOpen) {
              setPendingComment(responseData.pending_comment ?? null);
            }
            setReplyToId(null);
            setReplyToName(null);
            setIsCreating(false);
            refetch();
            onCommentAdded?.();
          }
        } else if (responseData.error?.message === "The account already exists") {
          setCommentModalOpen(true);
          setCommentModalStepState("existing");
          setSignupEmail(commentData.email);
          setSignupName(commentData.name);
          setIsCreating(false);
        } else {
          setErrorMessage(responseData.error?.message ?? "An error occurred");
          setIsCreating(false);
          setIsError(true);
        }
      } catch (error) {
        setErrorMessage(error.message);
        setIsCreating(false);
        setIsError(true);
      }
    },
    [client, refetch, onCommentAdded],
  );

  const handleSendEmailLink = useCallback(
    async (linkData) => {
      setIsSendingLink(true);
      const url = router("web.postLinkComment", { campaign: linkData.campaign_id });
      const req = request(url, "POST", csrf(document), linkData);

      try {
        const response = await fetch(req);
        const json = await response.json();
        const responseData = getResponseData(json);

        if (responseData.success === true) {
          setIsSendingLink(false);
          setCommentModalOpen(true);
          setCommentModalStepState("thankyou");
          if (Array.isArray(responseData.comments)) {
            setComments(responseData.comments);
          }
          setPendingComment(responseData.pending_comment ?? null);
          setReplyToId(null);
          setReplyToName(null);
          refetch();
        } else {
          setIsSendingLink(false);
        }
      } catch {
        setIsSendingLink(false);
      }
    },
    [refetch],
  );

  const handleResendEmailLink = useCallback(async (resendData) => {
    setIsResendingLink(true);
    const url = router("web.postRelinkComment", { campaign: resendData.campaign_id });
    const req = request(url, "POST", csrf(document), resendData);

    try {
      const response = await fetch(req);
      const json = await response.json();
      const responseData = getResponseData(json);

      if (responseData.success === true) {
        setIsResendingLink(false);
        setIsResendLinkFail(false);
        setIsResendLinkSent(true);
      } else {
        setIsResendingLink(false);
        setIsResendLinkFail(true);
        setIsResendLinkSent(false);
      }
    } catch {
      setIsResendingLink(false);
      setIsResendLinkFail(true);
      setIsResendLinkSent(false);
    }
  }, []);

  // When Turnstile returns a token, decide what to do
  useEffect(() => {
    const token = captchaToken.token;
    if (!token) {
      return;
    }

    // Logged-in: post immediately once token arrives
    if (isLoggedIn && pendingSubmit) {
      const payloadWithToken = { ...pendingSubmit, captchaToken: token };
      handleCreateComment(payloadWithToken);
      setPendingSubmit(null);
      resetTurnstileAndToken();
      return;
    }

    // Guest: keep modal open; token will be used for signup/login requests
    setIsRefreshing(false);
  }, [captchaToken.token, isLoggedIn, pendingSubmit, handleCreateComment, resetTurnstileAndToken]);

  // For logged-in users: if pendingSubmit is ready but token is absent, trigger a reset now
  useEffect(() => {
    if (isLoggedIn && pendingSubmit && !captchaToken.token && !isRefreshing && !hasTurnstileError) {
      requestVerification();
    }
  }, [
    isLoggedIn,
    pendingSubmit,
    captchaToken.token,
    isRefreshing,
    hasTurnstileError,
    requestVerification,
  ]);

  const handleTurnstileError = useCallback(() => {
    setHasTurnstileError(true);
    setIsRefreshing(false);
  }, []);

  const closeCommentModal = useCallback(() => {
    handleSetModalStatus(false);
    setIsRefreshing(false);
    setPendingSubmit(null);
  }, [handleSetModalStatus]);

  const contextValue = useMemo(
    () => ({
      campaignId,
      campaignTitle,
      campaignUpdateId,
      challengerId,
      captchaToken: captchaToken.token,
      commentModalOpen,
      commentModalStep,
      createComment: handleCreateComment,
      errorMessage,
      hasTurnstileError,
      isCreating,
      isError,
      isLoggedIn,
      isModalOpen: commentModalOpen,
      isRefreshing,
      isResendingLink,
      isResendLinkFail,
      isResendLinkSent,
      isSendingLink,
      onInteract: () => setHasInteracted(true),
      onPendingSubmit: setPendingSubmit,
      openModal: () => handleSetModalStatus(true),
      pendingComment,
      pendingSubmit,
      replyComment: handleReplyComment,
      replyToId,
      replyToName,
      resendEmailLink: handleResendEmailLink,
      sendEmailLink: handleSendEmailLink,
      setModalStatus: closeCommentModal,
      setModalStep: handleSetModalStep,
      setShowTurnstile: requestVerification,
      signupEmail,
      signupName,
      user,
    }),
    [
      campaignId,
      campaignTitle,
      campaignUpdateId,
      challengerId,
      captchaToken.token,
      commentModalOpen,
      commentModalStep,
      handleCreateComment,
      errorMessage,
      hasTurnstileError,
      isCreating,
      isError,
      isLoggedIn,
      isRefreshing,
      isResendingLink,
      isResendLinkFail,
      isResendLinkSent,
      isSendingLink,
      pendingComment,
      pendingSubmit,
      handleReplyComment,
      replyToId,
      replyToName,
      handleResendEmailLink,
      handleSendEmailLink,
      closeCommentModal,
      handleSetModalStatus,
      handleSetModalStep,
      requestVerification,
      signupEmail,
      signupName,
      user,
    ],
  );

  return (
    <CommentContext.Provider value={contextValue}>
      <div
        className={classNames("comments --v2", { "--campaign-update": Boolean(campaignUpdateId) })}
      >
        {!campaignUpdateId && (
          <h2 className="--large">
            {t("Comments")}
            {" "}
            <span className="comments__amount">
              (
              {comments.length}
              )
            </span>
          </h2>
        )}

        <div className="comments__form --v2">
          <CommentForm />
        </div>

        {isLoading === true && (
          <CommentItem
            commentText="Loading..."
            id={0}
            isDisabled
            isLoading
            name="Loading"
            timestamp={new Date().toISOString()}
          />
        )}

        {isLoading !== true && pendingComment !== null && (
          <CommentItem
            commentText={pendingComment.content}
            id={pendingComment.uuid ?? pendingComment.id}
            isPending
            name="Pending Comment"
            timestamp={pendingComment.created_at}
          />
        )}

        {isLoading !== true && <CommentList comments={comments} />}

        <CommentModal />

        {hasInteracted && (
          <div className="comments__turnstile">
            <TurnstileWrapper
              ref={turnstileRef}
              emitNullOnReset
              onChange={(token) => {
                setCaptchaToken({ token });
                setIsRefreshing(false);
              }}
              setTurnstileInteractive={() => setIsRefreshing(true)}
              captchaKey={TURNSTILE_KEY}
              onError={handleTurnstileError}
              action="comment"
            />
          </div>
        )}
      </div>
    </CommentContext.Provider>
  );
};

Comments.propTypes = {
  campaignId: PropTypes.oneOfType([PropTypes.number.isRequired, PropTypes.string.isRequired]),
  campaignTitle: PropTypes.string.isRequired,
  campaignUpdateId: PropTypes.string,
  challengerId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  user: PropTypes.object,
  onCommentAdded: PropTypes.func,
};

export default Comments;
